import { useState, useEffect, useCallback, useContext } from 'react';
import { classNames } from 'primereact/utils';
import { Redirect, Route, useHistory } from 'react-router-dom';

import AppTopbar from './AppTopbar';
import AppFooter from './AppFooter';

import AppMenu from './AppMenu';
import AppInlineProfile from './AppInlineProfile';

import { addLocale, locale } from 'primereact/api';
import translations from './i18n/locales/pl/primereact-translations.json';

import { AxiosConfigContainer } from './components/AxiosConfig';
import { useTranslation } from 'react-i18next';

import Measurements from './components/Measurements';

import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import './App.scss';
import { useDispatch, useSelector } from 'react-redux';
import { userActions } from './store/user-slice';
import usersService from './service/api/UsersService';

import { ChangesOfStates } from './components/Scales/ChangesOfStates/ChangesOfStates';
import { ScaleChart } from './components/Scales/Chart/Chart';

import { ReceptionsRecords } from './components/Suppliers/Receptions';

import { ScalesIndications } from './components/ScalesIndications/ScalesIndications';
import { AutoWeighings } from './components/Scales/AutoWeighings/AutoWeighings';
import { WeighingsList } from './components/WeighingsList/WeighingsList';
import { ScaleManagement } from './components/Configuration/ScaleManagement/ScaleManagement';
import { HostManagement } from './components/Configuration/HostManagement/HostManagement';
import NotFound from './pages/NotFound';
import { modeContext } from './services/ModeContext';
import { DivisionManagement } from './components/Configuration/Divisions/DivisionManagement';
import UserDialog from './components/Users/UserDialog';
import ChangePasswordDialog from './components/Users/ChangePasswordDialog';
import { OpenMeasurementsSeries } from './components/Scales/MeasurementsSeries/OpenMeasurementsSeries';
import { ClosedMeasurementsSeries } from './components/Scales/MeasurementsSeries/ClosedMeasurementsSeries';
import { MeasurementsSeriesDetails } from './components/Scales/MeasurementsSeries/MeasurementsSeriesDetails';
import { RoleManagement } from './components/Roles/RoleManagement';
import { hasOneOfPermissions, hasPermission } from './components/_shared/hasPermission';
import { UserManagement } from './components/Users/UserManagement';
import customersService from './service/api/CustomersService';
import { AnimalManagement } from './components/Animals/AnimalManagement';
import { AnimalDetailsPage } from './components/Animals/AnimalDetailsPage';
import { SETTINGS } from './enums/settings.enum';
import { CommunicationMode } from './enums/communication-mode.enum';
import { RootState } from './store';
import { PrinterManagement } from './components/Printers/PrinterManagement';
import { RawMaterialManagement } from './components/rawMaterial/RawMaterialManagement';
import { RecipeManagement } from './components/recipe/RecipeManagement';
import { OrderManagement } from './components/order/OrderManagement';
import { RawMaterialReportPage } from './components/rawMaterial/RawMaterialReportPage';
import { CustomerManagement } from './components/customer/CustomerManagement';
import { IoManagement } from './components/io/IoManagement';
import { GroupTemplateManagement } from './components/groupTemplate/GroupTemplateManagement';
import { GroupManagement } from './components/group/GroupManagement';
import { LicenseManagement } from './components/license/LicenseManagement';
import { UpdateLicenseKey } from './components/license/UpdateLicenseKey';
import AuthContext from './store/auth-context';
import { PinManagement } from './components/group/PinManagement';
import { ProductManagement } from './components/product/ProductManagement';
import { PackagedGoodsControlManagement } from './components/packagedGoodsControl/PackagedGoodsControlManagement';
import { ScaleInfoManagement } from './components/Scales/ScaleInfoManagement';
// <%-appJsImport%>

addLocale('pl', translations);

locale('pl');

const modeCtx = modeContext();

const App = () => {
  const [overlayMenuActive, setOverlayMenuActive] = useState(false);
  const [topbarMenuActive, setTopbarMenuActive] = useState(false);
  const [staticMenuDesktopInactive, setStaticMenuDesktopInactive] = useState(false);
  const [staticMenuMobileActive, setStaticMenuMobileActive] = useState(false);
  const [activeTopbarItem, setActiveTopbarItem] = useState(null);
  const [inlineMenuActive, setInlineMenuActive] = useState(false);
  const [userDialogVisible, setUserDialogVisible] = useState(false);
  const [changePasswordDialogVisible, setChangePasswordDialogVisible] = useState(false);
  const [updateLicenseDialogVisible, setUpdateLicenseDialogVisible] = useState(false);

  const [customers, setCustomers] = useState(null);

  const [configActive, setConfigActive] = useState(false);
  const dispatch = useDispatch();
  const loggedUserContext = useSelector((state: RootState) => state.user.context);
  const [initialized, setInitialized] = useState(false);
  const history = useHistory();

  const { i18n, t } = useTranslation();

  const profileMode = 'popup';
  const ripple = false;
  const menuActive = false;
  const menuMode = 'static';
  const darkMenu = true;
  const authCtx = useContext(AuthContext);

  const fetchUserContext = useCallback(async () => {
    try {
      const response = await usersService.getLoggedUserContext();
      const context = response.data;
      const language = context.language;
      if (['pl', 'en'].includes(language)) {
        i18n.changeLanguage(language);
        locale(language);
      }
      dispatch(userActions.setLoggedUserContext(context));

      if (hasPermission('CHANGE_CUSTOMER', context.permissions)) {
        const result = await customersService.getAll();
        setCustomers(result);
      }

      setInitialized(true);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        authCtx.logout();
      }
    }
  }, [dispatch, authCtx, i18n]);

  useEffect(() => {
    fetchUserContext();
  }, [fetchUserContext]);

  const setLanguage = useCallback(() => {
    i18n.changeLanguage('pl');
  }, [i18n]);

  useEffect(() => {
    setLanguage();
  }, [setLanguage]);

  let menuClick = false;
  let configClick = false;
  let topbarItemClick = false;
  let inlineMenuClick = false;

  const scaleMenuItems = loggedUserContext.scales.map((element) => {
    return {
      label: element?.name,
      items: [
        {
          label: t('app_labels.scale_chart'),
          to: `/${element?.id}/scale-chart`,
        },
        {
          label: t('app_labels.auto_weighings'),
          to: `/${element?.id}/auto-weighings`,
          visible: element.communicationMode === CommunicationMode.TRIGGERED_BY_US,
        },
        {
          label: t('measurementsSeries.header'),
          visible: element.communicationMode === CommunicationMode.TRIGGERED_BY_US,
          items: [
            {
              label: t('measurementsSeries.open'),
              to: `/${element?.id}/measurements-series/open`,
            },
            {
              label: t('measurementsSeries.closed'),
              to: `/${element?.id}/measurements-series/closed`,
            },
          ],
        },
        {
          label: t('app_labels.changes_of_states'),
          to: `/${element?.id}/changes-of-states`,
          visible: element.communicationMode === CommunicationMode.TRIGGERED_BY_US,
        },
      ],
    };
  });

  const recipesMenuItems = loggedUserContext.groups
    .filter((group) => group.scaleId && group.groupTemplateType === 'DOSAGE')
    .map((group) => {
      return {
        label: `${group.scaleName} - ${group.name}`,
        to: `/${group.id}/recipe`,
      };
    });

  const getByScaleId = (scaleId) => {
    for (const scale of loggedUserContext.scales) {
      if (scaleId === scale.id) {
        return scale;
      }
    }
  };

  const getByGroupId = (groupId: string) => {
    for (const group of loggedUserContext.groups) {
      if (groupId === group.id) {
        return group;
      }
    }
  };

  const menu = [
    {
      items: [
        {
          label: t('app_labels.scales_indications_dashboard'),
          to: `/scales-indications-dashboard`,
          visible: modeCtx.isOffline,
        },
      ],
    },
    {
      items: [
        {
          label: t('app_labels.scales_indications'),
          to: `/scales-indications`,
        },
      ],
    },
    {
      items: [
        {
          label: t('app_labels.weighing_list'),
          to: `/weighing-list`,
        },
      ],
    },
    {
      items: [
        {
          label: t('pins.header'),
          to: '/pins',
          visible: hasPermission('IO_PIN_VIEW', loggedUserContext.permissions),
        },
      ],
    },
    {
      items: [
        {
          label: t('app_labels.packaged_goods_control'),
          items: [
            {
              label: t('products.header'),

              to: '/product',
              visible: hasPermission('PRODUCT_MANAGEMENT', loggedUserContext.permissions),
            },
            {
              label: t('packagedGoodsControls.header'),

              to: '/packaged-goods-control',
              visible: hasPermission('PACKAGED_GOODS_CONTROL_MANAGEMENT', loggedUserContext.permissions),
            },
            {
              label: t('scaleInfo.header'),
              to: '/scale-info',
            },
          ],
        },
      ],
    },
    {
      visible: hasOneOfPermissions(
        [
          'ORDER_MANAGEMENT',
          'RECIPE_MANAGEMENT',
          'INGREDIENT_MANAGEMENT',
          'RAW_MATERIAL_MANAGEMENT',
          'VIEW_DOSAGE_REPORTS',
        ],
        loggedUserContext.permissions
      ),
      items: [
        {
          label: t('app_labels.dosage'),
          items: [
            {
              label: t('orders.header'),
              to: '/order',
              visible: hasPermission('ORDER_MANAGEMENT', loggedUserContext.permissions),
            },
            {
              label: t('recipes.header'),
              items: recipesMenuItems,
              visible: hasPermission('RECIPE_MANAGEMENT', loggedUserContext.permissions),
            },
            {
              label: t('rawMaterials.header'),
              to: '/raw-material',
              visible: hasPermission('RAW_MATERIAL_MANAGEMENT', loggedUserContext.permissions),
            },
            {
              label: t('rawMaterials.report_header'),
              to: '/raw-material-report',
              visible: hasPermission('VIEW_DOSAGE_REPORTS', loggedUserContext.permissions),
            },
          ],
        },
      ],
    },
    {
      label: t('app_labels.scales'),
      items: scaleMenuItems,
    },
    {
      visible: hasOneOfPermissions(
        [
          'HOST_MANAGEMENT',
          'SCALE_MANAGEMENT',
          'PRINTER_MANAGEMENT',
          'DIVISION_MANAGEMENT',
          'IO_MANAGEMENT',
          'GROUP_TEMPLATES_MANAGEMENT',
          'GROUPS_MANAGEMENT',
          'ANIMAL_MANAGEMENT',
        ],
        loggedUserContext.permissions
      ),
      items: [
        {
          label: t('app_labels.config'),
          items: [
            {
              label: t('hosts.header'),
              to: '/hosts',
              visible: modeCtx.hostsEnabled && hasPermission('HOST_MANAGEMENT', loggedUserContext.permissions),
            },
            {
              label: t('scales.header'),
              to: '/scales',
              visible: hasPermission('SCALE_MANAGEMENT', loggedUserContext.permissions),
            },
            {
              label: t('printers.header'),
              to: '/printers',
              visible: hasPermission('PRINTER_MANAGEMENT', loggedUserContext.permissions),
            },
            {
              label: t('divisions.header'),
              to: '/divisions',
              visible: hasPermission('DIVISION_MANAGEMENT', loggedUserContext.permissions),
            },
            {
              label: t('app_labels.io'),
              visible: hasOneOfPermissions(
                ['IO_MANAGEMENT', 'GROUP_TEMPLATES_MANAGEMENT', 'GROUPS_MANAGEMENT'],
                loggedUserContext.permissions
              ),
              items: [
                {
                  label: t('io.header'),
                  to: '/io',
                  visible: hasPermission('IO_MANAGEMENT', loggedUserContext.permissions),
                },
                {
                  label: t('groupTemplates.header'),
                  to: '/group-templates',
                  visible: hasPermission('GROUP_TEMPLATES_MANAGEMENT', loggedUserContext.permissions),
                },
                {
                  label: t('groups.header'),
                  to: '/groups',
                  visible: hasPermission('GROUPS_MANAGEMENT', loggedUserContext.permissions),
                },
              ],
            },
            {
              label: t('app_labels.animals'),
              to: `/animals`,
              visible: hasPermission('ANIMAL_MANAGEMENT', loggedUserContext.permissions),
            },
            // <%-appJsMenu%>
          ],
        },
      ],
    },
    {
      visible: hasOneOfPermissions(
        ['USER_MANAGEMENT', 'ROLE_MANAGEMENT', 'CUSTOMER_MANAGEMENT', 'LICENSE_MANAGEMENT'],
        loggedUserContext.permissions
      ),
      items: [
        {
          label: t('app_labels.administration'),
          items: [
            {
              label: t('users.header'),
              to: '/users',
              visible: hasPermission('USER_MANAGEMENT', loggedUserContext.permissions),
            },
            {
              label: t('roles.header'),
              to: '/roles',
              visible: hasPermission('ROLE_MANAGEMENT', loggedUserContext.permissions),
            },
            {
              label: t('customers.header'),
              to: '/customer',
              visible: hasPermission('CUSTOMER_MANAGEMENT', loggedUserContext.permissions),
            },
            {
              label: t('licenses.header'),
              to: '/license',
              visible: hasPermission('LICENSE_MANAGEMENT', loggedUserContext.permissions),
            },
          ],
        },
      ],
    },
  ];

  const routers = [
    {
      path: '/scales-indications',
      render: () => {
        return <ScalesIndications scales={loggedUserContext.scales} />;
      },
      meta: {
        breadcrumb: [
          {
            label: t('app_labels.scales_indications'),
          },
        ],
      },
    },
    {
      path: '/weighing-list',
      render: () => {
        return <WeighingsList scales={loggedUserContext.scales} />;
      },
      meta: {
        breadcrumb: [
          {
            label: t('app_labels.scales_indications'),
          },
        ],
      },
    },
    {
      path: '/users',
      exact: false,
      render: () => <UserManagement />,
      meta: {
        breadcrumb: [{}],
      },
    },
    {
      path: '/roles',
      render: () => <RoleManagement />,
      meta: {
        breadcrumb: [{}],
      },
    },
    { path: '/:scaleId/measurements', component: Measurements },
    {
      path: '/:scaleId/changes-of-states',
      render: (e: any) => {
        let scaleName = getByScaleId(e.match?.params?.scaleId)?.name;

        let weighingUnit = getByScaleId(e.match?.params?.scaleId)?.weighingUnit;

        if ((scaleName || scaleName === '') && (weighingUnit || weighingUnit === '')) {
          return <ChangesOfStates scaleName={scaleName} weighingUnit={weighingUnit.toLowerCase()} />;
        }

        return <NotFound />;
      },
      meta: {
        breadcrumb: [
          {
            parent: t('app_labels.scales'),
            label: t('app_labels.changes_of_states'),
          },
        ],
      },
    },
    {
      path: '/:scaleId/scale-chart',
      render: (e: any) => {
        const scale = getByScaleId(e.match?.params?.scaleId);
        return scale ? <ScaleChart scaleId={scale.id} scaleName={scale.name} /> : <NotFound />;
      },

      meta: {
        breadcrumb: [
          {
            parent: t('app_labels.scales'),
            label: t('app_labels.scale_chart'),
          },
        ],
      },
    },
    {
      path: '/:scaleId/auto-weighings',
      render: (e: any) => {
        const scaleName = getByScaleId(e.match?.params?.scaleId)?.name;

        return scaleName || scaleName === '' ? <AutoWeighings scaleName={scaleName} /> : <NotFound />;
      },
      meta: {
        breadcrumb: [
          {
            parent: t('app_labels.scales'),
            label: t('app_labels.auto_weighings'),
          },
        ],
      },
    },
    {
      path: '/:scaleId/measurements-series/open',
      render: (e: any) => {
        const scale = getByScaleId(e.match?.params?.scaleId);

        return scale ? <OpenMeasurementsSeries scaleId={scale.id} scaleName={scale.name} /> : <NotFound />;
      },
      meta: {
        breadcrumb: [
          {
            parent: t('app_labels.scales'),
            label: t('measurementsSeries.open'),
          },
        ],
      },
    },
    {
      path: '/:scaleId/measurements-series/closed',
      render: (e: any) => {
        const scale = getByScaleId(e.match?.params?.scaleId);

        return scale ? <ClosedMeasurementsSeries scaleId={scale.id} scaleName={scale.name} /> : <NotFound />;
      },
      meta: {
        breadcrumb: [
          {
            parent: t('app_labels.scales'),
            label: t('measurementsSeries.open'),
          },
        ],
      },
    },
    {
      path: '/measurements-series/:seriesId/details',
      render: (e: any) => <MeasurementsSeriesDetails seriesId={e.match?.params?.seriesId} />,
    },
    {
      path: '/suppliers-records',
      render: () => <ReceptionsRecords />,
      meta: {
        breadcrumb: [
          {
            parent: t('app_labels.suppliers'),
            label: t('app_labels.suppliers_list'),
          },
        ],
      },
    },
    {
      path: '/hosts',
      render: () => {
        return modeCtx.hostsEnabled ? <HostManagement /> : <NotFound />;
      },
    },
    {
      path: '/scales',
      render: () => {
        return <ScaleManagement reloadMenu={fetchUserContext} />;
      },
    },
    {
      path: '/printers',
      render: () => {
        return <PrinterManagement />;
      },
    },
    {
      path: '/io',
      render: () => {
        return <IoManagement />;
      },
    },
    {
      path: '/group-templates',
      render: () => {
        return <GroupTemplateManagement />;
      },
    },
    {
      path: '/groups',
      render: () => {
        return <GroupManagement />;
      },
    },
    {
      path: '/pins',
      render: () => {
        return <PinManagement />;
      },
    },
    {
      path: '/scale-info/:scaleId?',
      render: () => {
        return <ScaleInfoManagement />;
      },
    },
    {
      path: '/divisions',
      render: () => {
        return <DivisionManagement />;
      },
    },
    {
      path: '/animal-details/:id',
      render: () => {
        return <AnimalDetailsPage />;
      },
    },
    {
      path: '/animals',
      render: () => {
        return <AnimalManagement />;
      },
    },
    {
      path: '/raw-material',
      component: RawMaterialManagement,
      render: () => {
        return <RawMaterialManagement />;
      },
    },
    {
      path: '/raw-material-report',
      component: RawMaterialReportPage,
      render: () => {
        return <RawMaterialReportPage />;
      },
    },
    {
      path: '/:groupId/recipe',
      component: RecipeManagement,
      render: (e: any) => {
        const group = getByGroupId(e.match?.params?.groupId);
        return <RecipeManagement group={group} />;
      },
    },
    {
      path: '/order',
      component: OrderManagement,
      render: () => {
        return <OrderManagement />;
      },
    },
    {
      path: '/customer',
      component: CustomerManagement,
      render: () => {
        return <CustomerManagement />;
      },
    },
    {
      path: '/license',
      component: LicenseManagement,
      render: () => {
        return <LicenseManagement />;
      },
    },
    {
      path: '/product',
      component: ProductManagement,
      render: () => {
        return <ProductManagement />;
      },
    },
    {
      path: '/packaged-goods-control',
      component: PackagedGoodsControlManagement,
      render: () => {
        return <PackagedGoodsControlManagement />;
      },
    },
    // <%-appJsRouters%>
  ];

  const onDocumentClick = () => {
    if (!topbarItemClick) {
      setActiveTopbarItem(null);
      setTopbarMenuActive(false);
    }

    if (!menuClick) {
      hideOverlayMenu();
    }

    if (configActive && !configClick) {
      setConfigActive(false);
    }

    inlineMenuClick = false;
    configClick = false;
    topbarItemClick = false;
    menuClick = false;
  };

  const onMenuitemClick = (event) => {
    if (!event.item.items) {
      hideOverlayMenu();
    }
  };

  const onMenuClick = () => {
    menuClick = true;

    if (inlineMenuActive && !inlineMenuClick) {
      setInlineMenuActive(false);
    }
  };

  const isMenuVisible = () => {
    if (isDesktop()) {
      if (menuMode === 'static') return !staticMenuDesktopInactive;
      else if (menuMode === 'overlay') return overlayMenuActive;
      else return true;
    } else {
      return true;
    }
  };

  const onMenuButtonClick = (event) => {
    menuClick = true;
    setTopbarMenuActive(false);

    if (isOverlay() && !isMobile()) {
      setOverlayMenuActive((prevOverlayMenuActive) => !prevOverlayMenuActive);
    } else {
      if (isDesktop()) {
        setStaticMenuDesktopInactive((prevStaticMenuDesktopInactive) => !prevStaticMenuDesktopInactive);
      } else {
        setStaticMenuMobileActive((prevStaticMenuMobileActive) => !prevStaticMenuMobileActive);
      }
    }

    event.preventDefault();
  };

  const onProfileButtonClick = () => {
    setInlineMenuActive((prevInlineMenuActive) => !prevInlineMenuActive);
    inlineMenuClick = true;
  };

  const onTopbarMenuButtonClick = (event) => {
    topbarItemClick = true;
    setTopbarMenuActive((prevTopbarMenuActive) => !prevTopbarMenuActive);

    hideOverlayMenu();

    event.preventDefault();
  };

  const onTopbarItemClick = (event, item) => {
    topbarItemClick = true;

    if (activeTopbarItem === item) {
      setActiveTopbarItem(null);
    } else {
      setActiveTopbarItem(item);
    }

    event.preventDefault();
  };

  const hideOverlayMenu = () => {
    setOverlayMenuActive(false);
    setStaticMenuMobileActive(false);
  };

  const isDesktop = () => {
    return window.innerWidth > 896;
  };

  const isMobile = () => {
    return window.innerWidth <= 896;
  };

  const isOverlay = () => {
    return false;
  };

  const isHorizontal = () => {
    return false;
  };

  const isSlim = () => {
    return false;
  };

  const isStatic = () => {
    return menuMode === 'static';
  };

  const hasInlineProfile = false;

  const containerClassName = classNames('layout-wrapper', {
    'layout-static': isStatic(),
    'layout-overlay': isOverlay(),
    'layout-overlay-active': overlayMenuActive,
    'layout-horizontal': isHorizontal(),
    'layout-slim': isSlim(),
    'layout-static-inactive': staticMenuDesktopInactive,
    'layout-mobile-active': staticMenuMobileActive,
    'layout-menu-dark': darkMenu,
    'layout-menu-light': !darkMenu,
    'p-input-filled': false,
    'p-ripple-disabled': !ripple,
  });

  const menuContainerClassName = classNames('layout-menu-container', {
    'layout-menu-container-inactive': !isMenuVisible(),
  });

  const showUserDialog = () => {
    setUserDialogVisible(true);
  };

  const hideUserDialog = () => {
    setUserDialogVisible(false);
    fetchUserContext();
  };

  const showChangePasswordDialog = () => {
    setChangePasswordDialogVisible(true);
  };

  const hideChangePasswordDialog = () => {
    setChangePasswordDialogVisible(false);
  };

  const showUpdateLicenseDialog = () => {
    setUpdateLicenseDialogVisible(true);
  };

  const hideUpdateLicenseDialog = () => {
    setUpdateLicenseDialogVisible(false);
  };

  return (
    <div className={containerClassName} onClick={onDocumentClick}>
      <AxiosConfigContainer />
      {initialized && (
        <>
          <AppTopbar
            topbarMenuActive={topbarMenuActive}
            activeTopbarItem={activeTopbarItem}
            onMenuButtonClick={onMenuButtonClick}
            onTopbarMenuButtonClick={onTopbarMenuButtonClick}
            onTopbarItemClick={onTopbarItemClick}
            onShowUserDetailsClick={showUserDialog}
            onChangePasswordClick={showChangePasswordDialog}
            onUpdateLicenseClick={showUpdateLicenseDialog}
            isHorizontal={isHorizontal()}
            profileMode={profileMode}
            isMobile={isMobile}
            allCustomers={customers}
            customerId={loggedUserContext.customerId}
            onCustomerChange={async (customerId) => {
              await customersService.reassign(customerId);
              fetchUserContext();
            }}
          />

          <div className={menuContainerClassName} onClick={onMenuClick}>
            <div className="layout-menu-logo">
              <button className="p-link" onClick={() => history.push(SETTINGS.DEFAULT_PAGE)}>
                <img
                  id="layout-menu-logo"
                  src="assets/layout/images/logo-white.png"
                  alt="application logo"
                  width="220"
                />
              </button>
            </div>
            <div className="layout-menu-wrapper">
              <div className="menu-scroll-content">
                {hasInlineProfile && (
                  <AppInlineProfile inlineMenuActive={inlineMenuActive} onProfileButtonClick={onProfileButtonClick} />
                )}
                <AppMenu
                  model={menu}
                  menuMode={menuMode}
                  active={menuActive}
                  onMenuitemClick={onMenuitemClick}
                  onRootMenuitemClick={() => {}}
                />
              </div>
            </div>
          </div>

          <div className="layout-main">
            <div className="layout-content">
              {routers.map((router, index) => {
                if (router.exact) {
                  return <Route key={`router${index}`} path={router.path} exact render={router.render} />;
                }

                return <Route key={`router${index}`} path={router.path} render={router.render} />;
              })}

              <Route exact path="/">
                <Redirect to={SETTINGS.DEFAULT_PAGE as any} />
              </Route>
            </div>

            <UserDialog dialogVisible={userDialogVisible} closeAddEditModal={hideUserDialog} user={loggedUserContext} />

            <ChangePasswordDialog
              dialogVisible={changePasswordDialogVisible}
              closeAddEditModal={hideChangePasswordDialog}
            />

            <UpdateLicenseKey dialogVisible={updateLicenseDialogVisible} onHide={hideUpdateLicenseDialog} />

            <AppFooter />
          </div>

          {staticMenuMobileActive && <div className="layout-mask"></div>}
        </>
      )}
    </div>
  );
};

export default App;
